import React, { useState } from 'react';
import {
  FiAlertOctagon,
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiFileText,
  FiLoader,
  FiRefreshCw,
  FiRotateCcw,
} from 'react-icons/fi';

import type { KnowledgeGapPattern } from '../../services/agentAnalyticsApi';

interface GapCardProps {
  gap: KnowledgeGapPattern;
  /** Chamado quando o cliente clica em "Responder este gap" */
  onResolve: (gap: KnowledgeGapPattern) => void;
  /** Chamado quando o cliente clica em "Desmarcar resolvido" (só em gaps já resolvidos) */
  onUnresolve: (gap: KnowledgeGapPattern) => void;
  /** Se o cliente já confirmou a ação de unresolve (loading state) */
  unresolving?: boolean;
  /**
   * Quando `true`, este gap está com draft sendo gerado pelo LLM
   * (antes do modal abrir). Botão fica disabled + mostra spinner +
   * texto muda pra "Gerando sugestão...".
   */
  generatingDraft?: boolean;
}

const SEVERITY_CONFIG: Record<
  'low' | 'medium' | 'high',
  { label: string; color: string }
> = {
  low: { label: 'Baixa', color: '#6b7280' },
  medium: { label: 'Média', color: '#f59e0b' },
  high: { label: 'Alta', color: '#ef4444' },
};

/**
 * Card narrativo de uma lacuna de conhecimento detectada. Mostra:
 * - Label + contagem de ocorrências
 * - Severity badge
 * - Expansor com exemplos reais de traces problemáticos
 * - CTA "Responder este gap" (ou "Desmarcar resolvido" se já resolvido)
 *
 * Gaps resolvidos ficam com aparência muted + CTA invertido.
 */
export const GapCard: React.FC<GapCardProps> = ({
  gap,
  onResolve,
  onUnresolve,
  unresolving,
  generatingDraft,
}) => {
  const [expanded, setExpanded] = useState(false);
  const severityConfig = SEVERITY_CONFIG[gap.severity];
  const resolved = gap.isResolved;
  const reappeared = gap.previouslyResolved;
  const documentMissing = gap.resolution?.databaseDocumentMissing === true;

  // Cor de fundo: verde se resolvido, amarelo se reapareceu, default caso contrário
  const cardBg = resolved
    ? 'rgba(16, 185, 129, 0.02)'
    : reappeared
      ? 'rgba(245, 158, 11, 0.04)'
      : 'var(--theme-card-background)';
  const cardBorder = resolved
    ? 'rgba(16, 185, 129, 0.2)'
    : reappeared
      ? 'rgba(245, 158, 11, 0.3)'
      : 'var(--theme-card-border)';

  // Ícone do header: check verde (resolvido), refresh amarelo (reapareceu),
  // alert da severity (default)
  const headerIcon = resolved ? (
    <FiCheckCircle className="h-5 w-5" />
  ) : reappeared ? (
    <FiRefreshCw className="h-5 w-5" />
  ) : (
    <FiAlertOctagon className="h-5 w-5" />
  );
  const headerIconBg = resolved
    ? 'rgba(16, 185, 129, 0.12)'
    : reappeared
      ? 'rgba(245, 158, 11, 0.12)'
      : `${severityConfig.color}20`;
  const headerIconColor = resolved
    ? '#10b981'
    : reappeared
      ? '#f59e0b'
      : severityConfig.color;

  return (
    <div
      className="rounded-lg border p-4 transition-colors"
      style={{
        backgroundColor: cardBg,
        borderColor: cardBorder,
        opacity: resolved ? 0.85 : 1,
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div
            className="flex-shrink-0 p-2 rounded-md"
            style={{
              backgroundColor: headerIconBg,
              color: headerIconColor,
            }}
          >
            {headerIcon}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3
                className="text-sm font-semibold"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {gap.label}
              </h3>
              {!resolved && !reappeared && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded font-medium"
                  style={{
                    backgroundColor: severityConfig.color,
                    color: '#ffffff',
                  }}
                >
                  {severityConfig.label}
                </span>
              )}
              {reappeared && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded font-medium"
                  style={{ backgroundColor: '#f59e0b', color: '#ffffff' }}
                >
                  Reapareceu
                </span>
              )}
              {resolved && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded font-medium"
                  style={{ backgroundColor: '#10b981', color: '#ffffff' }}
                >
                  Resolvido
                </span>
              )}
            </div>
            <p
              className="text-xs"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {gap.count}{' '}
              {gap.count === 1 ? 'ocorrência' : 'ocorrências'} no período
              {resolved && gap.resolution
                ? ` · resolvido em ${formatRelativeDate(gap.resolution.resolvedAt)}`
                : ''}
              {reappeared && gap.resolution
                ? ` · resolvido em ${formatRelativeDate(gap.resolution.resolvedAt)} mas reapareceu`
                : ''}
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="flex-shrink-0">
          {resolved ? (
            <button
              type="button"
              onClick={() => onUnresolve(gap)}
              disabled={unresolving}
              className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium border transition-colors"
              style={{
                borderColor: 'var(--theme-card-border)',
                color: 'var(--theme-muted-text)',
                cursor: unresolving ? 'wait' : 'pointer',
              }}
            >
              <FiRotateCcw className="h-3 w-3" />
              Desmarcar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onResolve(gap)}
              disabled={generatingDraft}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors"
              style={{
                backgroundColor: generatingDraft ? '#a78bfa' : '#8b5cf6',
                color: '#ffffff',
                cursor: generatingDraft ? 'wait' : 'pointer',
              }}
              data-testid={`gap-card-resolve-${gap.gapKey}`}
            >
              {generatingDraft ? (
                <>
                  <FiLoader className="h-3 w-3 animate-spin" />
                  Gerando sugestão...
                </>
              ) : (
                'Responder este gap'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Banner de reaparecimento */}
      {reappeared && (
        <div
          className="mt-3 rounded-md px-3 py-2 text-xs flex items-start gap-2"
          style={{
            backgroundColor: 'rgba(245, 158, 11, 0.08)',
            color: '#92400e',
            border: '1px solid rgba(245, 158, 11, 0.2)',
          }}
        >
          <FiAlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Este gap reapareceu.</strong>{' '}
            {gap.samplesAfterResolution > 0 && (
              <>
                Detectamos {gap.samplesAfterResolution}{' '}
                {gap.samplesAfterResolution === 1
                  ? 'nova ocorrência'
                  : 'novas ocorrências'}{' '}
                após a resolução. A solução anterior pode não ter funcionado
                ou o agente continua falhando — vale revisar.
              </>
            )}
          </div>
        </div>
      )}

      {/* Banner de documento removido */}
      {documentMissing && (
        <div
          className="mt-3 rounded-md px-3 py-2 text-xs flex items-start gap-2"
          style={{
            backgroundColor: 'rgba(99, 102, 241, 0.06)',
            color: '#4338ca',
            border: '1px solid rgba(99, 102, 241, 0.2)',
          }}
        >
          <FiFileText className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            O documento criado para resolver este gap foi removido da base.
            Você pode desmarcar e responder novamente.
          </div>
        </div>
      )}

      {/* Footer expansível */}
      <div
        className="mt-3 pt-3 border-t"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs font-medium"
          style={{ color: '#8b5cf6' }}
        >
          {expanded ? (
            <>
              <FiChevronUp className="h-3 w-3" />
              Esconder exemplos
            </>
          ) : (
            <>
              <FiChevronDown className="h-3 w-3" />
              Ver {gap.samples.length}{' '}
              {gap.samples.length === 1 ? 'exemplo' : 'exemplos'}
            </>
          )}
        </button>

        {expanded && (
          <div className="mt-3 space-y-2">
            {gap.samples.map((sample) => (
              <div
                key={sample.traceId}
                className="rounded px-3 py-2 text-xs space-y-2"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  color: 'var(--theme-muted-text)',
                }}
              >
                <div
                  className="font-semibold"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {formatDateTime(sample.timestamp)}
                </div>
                {sample.customerMessage && (
                  <div>
                    <div
                      className="font-medium mb-0.5"
                      style={{ color: '#0369a1' }}
                    >
                      Cliente:
                    </div>
                    <div className="italic">
                      &ldquo;{sample.customerMessage}&rdquo;
                    </div>
                  </div>
                )}
                <div>
                  <div
                    className="font-medium mb-0.5"
                    style={{ color: '#7c3aed' }}
                  >
                    Agente:
                  </div>
                  <div className="italic">&ldquo;{sample.snippet}&rdquo;</div>
                </div>
              </div>
            ))}

            {/* Link pro document criado, se houver */}
            {resolved && gap.resolution?.databaseDocumentId && (
              <div
                className="text-xs flex items-center gap-1"
                style={{ color: '#10b981' }}
              >
                <FiExternalLink className="h-3 w-3" />
                Resolvido com novo documento na base
              </div>
            )}

            {resolved && gap.resolution?.note && (
              <div
                className="text-xs italic rounded px-3 py-2"
                style={{
                  backgroundColor: 'rgba(16, 185, 129, 0.06)',
                  color: 'var(--theme-muted-text)',
                }}
              >
                <span className="font-semibold">Nota: </span>
                {gap.resolution.note}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function formatDateTime(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatRelativeDate(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
